import React, { useEffect, useState } from 'react';
import { Loader2, MessageCircle, CreditCard, Building2, ExternalLink } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { masterFirebaseService } from '../services/firebaseService';
import { AmbienteGateway, GatewayConfig, RecursoContratado, SubcontaAsaasInfo } from '../types';
import { CATALOGO_GATEWAYS, CampoGateway, rotuloMetodos, urlWebhook } from '../data/gateways';

interface ConfiguracaoEscolaProps {
  cnpj: string;
}

const gatewayVazio = (): GatewayConfig => ({
  ativo: false,
  ambienteAtivo: 'homologacao',
  homologacao: {},
  producao: {},
});

const rotuloStatusSubconta = (status?: string) => {
  switch (status) {
    case 'aprovada': return 'Aprovada';
    case 'recusada': return 'Recusada';
    case 'em_analise': return 'Em análise';
    case 'docs_pendentes': return 'Documentos pendentes';
    case 'criada': return 'Criada';
    default: return 'Não criada';
  }
};

const ConfiguracaoEscola: React.FC<ConfiguracaoEscolaProps> = ({ cnpj }) => {
  const [whatsapp, setWhatsapp] = useState<RecursoContratado>({ ativo: true, definido: false });
  const [gateways, setGateways] = useState<Record<string, GatewayConfig>>({});
  const [subconta, setSubconta] = useState<SubcontaAsaasInfo | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvandoWhatsapp, setSalvandoWhatsapp] = useState(false);
  const [salvandoId, setSalvandoId] = useState<string | null>(null);
  const [testandoId, setTestandoId] = useState<string | null>(null);
  const [subcontaBusy, setSubcontaBusy] = useState(false);
  const [aviso, setAviso] = useState('');
  const [erro, setErro] = useState('');

  const carregarSubconta = async (ambiente: AmbienteGateway) => {
    const info = await masterFirebaseService.getSubcontaAsaas(cnpj, ambiente);
    setSubconta(info);
  };

  useEffect(() => {
    let ativo = true;
    const carregar = async () => {
      setCarregando(true);
      setErro('');
      try {
        const [recurso, salvos] = await Promise.all([
          masterFirebaseService.getRecurso(cnpj, 'whatsapp'),
          masterFirebaseService.getGateways(cnpj),
        ]);
        if (!ativo) return;
        setWhatsapp(recurso);
        setGateways(salvos);
        const ambiente = salvos.asaas?.ambienteAtivo || 'homologacao';
        const info = await masterFirebaseService.getSubcontaAsaas(cnpj, ambiente);
        if (ativo) setSubconta(info);
      } catch (error) {
        console.error('Erro ao carregar configuração da escola:', error);
        if (ativo) setErro('Não foi possível carregar recursos e gateways desta escola.');
      } finally {
        if (ativo) setCarregando(false);
      }
    };
    carregar();
    return () => {
      ativo = false;
    };
  }, [cnpj]);

  const alterarWhatsapp = async () => {
    const proximo = !whatsapp.ativo;
    setSalvandoWhatsapp(true);
    setAviso('');
    setErro('');
    try {
      await masterFirebaseService.salvarRecurso(cnpj, 'whatsapp', proximo);
      setWhatsapp({ ativo: proximo, definido: true, atualizadoEm: new Date().toISOString() });
      setAviso(proximo
        ? 'WhatsApp ligado. O menu de Integração aparece no painel.'
        : 'WhatsApp desligado. O menu de Integração fica oculto no painel.');
    } catch (error) {
      console.error('Erro ao salvar recurso:', error);
      setErro('Não foi possível gravar o recurso. O usuário do manager precisa estar cadastrado nesta escola.');
    } finally {
      setSalvandoWhatsapp(false);
    }
  };

  const atualizarGateway = (id: string, parcial: Partial<GatewayConfig>) => {
    setGateways((atual) => {
      const base = atual[id] || gatewayVazio();
      const proximo = { ...base, ...parcial };
      const todos = { ...atual, [id]: proximo };
      if (parcial.ativo) {
        Object.keys(todos).forEach((outro) => {
          if (outro !== id) todos[outro] = { ...todos[outro], ativo: false };
        });
      }
      return todos;
    });
    if (id === 'asaas' && parcial.ambienteAtivo) {
      carregarSubconta(parcial.ambienteAtivo).catch(() => undefined);
    }
  };

  const atualizarCredencial = (id: string, ambiente: AmbienteGateway, chave: string, valor: string) => {
    setGateways((atual) => {
      const base = atual[id] || gatewayVazio();
      return {
        ...atual,
        [id]: {
          ...base,
          [ambiente]: {
            ...base[ambiente],
            [chave]: valor,
          },
        },
      };
    });
  };

  const testarConexao = async (id: string, ambiente: AmbienteGateway) => {
    setTestandoId(id);
    setAviso('');
    setErro('');
    try {
      const chamar = httpsCallable<{ cnpj: string; gatewayId: string; ambiente: AmbienteGateway }, { mensagem?: string }>(functions, 'testarGateway');
      const resposta = await chamar({ cnpj, gatewayId: id, ambiente });
      setAviso(resposta.data.mensagem || 'Conexão confirmada.');
    } catch (error: unknown) {
      const mensagem = error instanceof Error ? error.message : '';
      setErro(mensagem || 'Não foi possível testar a conexão. Salve o gateway e confira as credenciais.');
    } finally {
      setTestandoId(null);
    }
  };

  const salvarGateway = async (id: string) => {
    const config = gateways[id] || gatewayVazio();
    setSalvandoId(id);
    setAviso('');
    setErro('');
    try {
      await masterFirebaseService.salvarGateway(cnpj, id, config);
      if (config.ativo) {
        setGateways((atual) => {
          const proximo = { ...atual };
          Object.keys(proximo).forEach((outro) => {
            if (outro !== id) proximo[outro] = { ...proximo[outro], ativo: false };
          });
          proximo[id] = { ...config, atualizadoEm: new Date().toISOString() };
          return proximo;
        });
      }
      setAviso('Gateway gravado. Credenciais e endereço do aviso atualizados.');
      if (id === 'asaas') await carregarSubconta(config.ambienteAtivo);
    } catch (error) {
      console.error('Erro ao salvar gateway:', error);
      setErro('Não foi possível gravar o gateway. O usuário do manager precisa estar cadastrado nesta escola.');
    } finally {
      setSalvandoId(null);
    }
  };

  const acaoSubconta = async (acao: 'criar' | 'documentos' | 'status') => {
    const ambiente = gateways.asaas?.ambienteAtivo || 'homologacao';
    setSubcontaBusy(true);
    setAviso('');
    setErro('');
    try {
      const nomeFn =
        acao === 'criar' ? 'criarSubcontaAsaas'
          : acao === 'documentos' ? 'sincronizarDocumentosSubconta'
            : 'consultarStatusSubconta';
      const chamar = httpsCallable(functions, nomeFn);
      const resposta = await chamar({ empresaId: cnpj, ambiente });
      const data = (resposta.data || {}) as {
        statusOnboarding?: string;
        onboardingUrls?: SubcontaAsaasInfo['onboardingUrls'];
        accountId?: string;
      };
      await carregarSubconta(ambiente);
      if (acao === 'criar') {
        setAviso(`Subconta criada (${data.accountId || 'ok'}). Em seguida sincronize os documentos.`);
      } else if (acao === 'documentos') {
        const qtd = data.onboardingUrls?.length || 0;
        setAviso(qtd > 0
          ? `${qtd} link(s) de onboarding disponíveis. Abra-os para a escola enviar os documentos.`
          : 'Nenhum link de onboarding pendente no momento. Consulte o status.');
      } else {
        setAviso(`Status da subconta: ${rotuloStatusSubconta(data.statusOnboarding)}.`);
      }
    } catch (error: unknown) {
      const err = error as { message?: string; code?: string };
      const mensagem = String(err.message || '')
        .replace(/^FirebaseError:\s*/i, '')
        .replace(/^Error:\s*/i, '')
        .trim();
      setErro(mensagem || 'Não foi possível executar a ação da subconta.');
    } finally {
      setSubcontaBusy(false);
    }
  };

  if (carregando) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-center gap-3 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
        Carregando recursos e gateways
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {erro && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{erro}</div>
      )}
      {aviso && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">{aviso}</div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Recursos</h2>
        </div>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-900">WhatsApp</p>
            <p className="text-sm text-gray-500 mt-1">
              {whatsapp.definido
                ? (whatsapp.ativo
                  ? 'Ativo. O menu de Integração aparece no painel.'
                  : 'Desligado. O menu de Integração fica oculto no painel.')
                : 'Ainda não configurado. O menu de Integração continua visível até ser desligado.'}
            </p>
          </div>
          <button
            type="button"
            onClick={alterarWhatsapp}
            disabled={salvandoWhatsapp}
            className={`btn px-4 py-2 ${whatsapp.ativo ? 'btn-primary' : 'btn-outline'}`}
          >
            {salvandoWhatsapp ? 'Salvando' : whatsapp.ativo ? 'Ligado' : 'Desligado'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <div className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Gateways</h2>
        </div>
        <p className="text-sm text-gray-500">
          Cada gateway guarda homologação e produção. Só um fica ativo. No Asaas, prefira criar a subconta da escola (white label) em vez de colar token manual.
        </p>

        {CATALOGO_GATEWAYS.map((item) => {
          const config = gateways[item.id] || gatewayVazio();
          if (!item.editavel) {
            return (
              <div key={item.id} className="rounded-lg border border-gray-200 p-4 opacity-70">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900">{item.nome}</p>
                  <span className="text-xs text-gray-500">Sem configuração nesta etapa</span>
                </div>
              </div>
            );
          }

          return (
            <div key={item.id} className="rounded-lg border border-gray-200 p-4 space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">{item.nome}</p>
                  {config.ativo && (
                    <p className="text-xs text-primary-700 mt-1">
                      Ambiente em uso: {config.ambienteAtivo === 'producao' ? 'Produção' : 'Homologação'}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => atualizarGateway(item.id, { ativo: !config.ativo })}
                  className={`btn px-3 py-1.5 text-xs ${config.ativo ? 'btn-primary' : 'btn-outline'}`}
                >
                  {config.ativo ? 'Ativo' : 'Inativo'}
                </button>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-600 mb-2">Ambiente em uso</p>
                <div className="flex gap-2">
                  {(['homologacao', 'producao'] as AmbienteGateway[]).map((ambiente) => (
                    <button
                      key={ambiente}
                      type="button"
                      onClick={() => atualizarGateway(item.id, { ambienteAtivo: ambiente })}
                      className={`btn px-3 py-1.5 text-xs ${config.ambienteAtivo === ambiente ? 'btn-primary' : 'btn-outline'}`}
                    >
                      {ambiente === 'homologacao' ? 'Homologação' : 'Produção'}
                    </button>
                  ))}
                </div>
              </div>

              {item.id === 'asaas' && (
                <div className="rounded-lg border border-primary-100 bg-primary-50/40 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary-700" />
                    <p className="text-sm font-medium text-gray-900">Subconta Asaas (white label)</p>
                  </div>
                  <p className="text-xs text-gray-600">
                    Cria a conta da escola no Asaas pela API da Victall. Cobranças nascem na subconta com split da taxa para a plataforma. Use uma escola cliente — não a conta mãe Victall. A escola precisa ter CNPJ, e-mail, telefone, CEP, logradouro e bairro cadastrados.
                  </p>
                  <div className="grid gap-2 text-xs text-gray-700 md:grid-cols-2">
                    <p><span className="font-medium">Modo:</span> {subconta?.modo === 'subconta' ? 'Subconta' : (subconta?.modo || 'não definido')}</p>
                    <p><span className="font-medium">Status:</span> {rotuloStatusSubconta(subconta?.statusOnboarding)}</p>
                    <p className="break-all"><span className="font-medium">Account:</span> {subconta?.accountId || '—'}</p>
                    <p className="break-all"><span className="font-medium">Wallet:</span> {subconta?.walletId || '—'}</p>
                  </div>
                  {subconta?.statusDetalhe && (
                    <p className="text-xs text-gray-600">
                      Comercial: {subconta.statusDetalhe.commercialInfo || '—'} · Docs: {subconta.statusDetalhe.documentation || '—'} · Geral: {subconta.statusDetalhe.general || '—'}
                    </p>
                  )}
                  {!!subconta?.onboardingUrls?.length && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-700">Links de documentos</p>
                      {subconta.onboardingUrls.map((link) => (
                        <a
                          key={link.id}
                          href={link.onboardingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-primary-700 hover:underline break-all"
                        >
                          <ExternalLink className="h-3 w-3 shrink-0" />
                          {link.title || link.type}
                        </a>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={subcontaBusy || subconta?.modo === 'subconta'}
                      onClick={() => acaoSubconta('criar')}
                      className="btn btn-primary px-3 py-1.5 text-xs"
                    >
                      {subcontaBusy ? 'Aguarde' : 'Criar subconta'}
                    </button>
                    <button
                      type="button"
                      disabled={subcontaBusy || subconta?.modo !== 'subconta'}
                      onClick={() => acaoSubconta('documentos')}
                      className="btn btn-outline px-3 py-1.5 text-xs"
                    >
                      Sincronizar documentos
                    </button>
                    <button
                      type="button"
                      disabled={subcontaBusy || subconta?.modo !== 'subconta'}
                      onClick={() => acaoSubconta('status')}
                      className="btn btn-outline px-3 py-1.5 text-xs"
                    >
                      Atualizar status
                    </button>
                  </div>
                  {config.ambienteAtivo === 'producao' && subconta?.modo === 'subconta' && subconta.statusOnboarding !== 'aprovada' && (
                    <p className="text-xs text-amber-700">
                      Em produção, a cobrança só libera com status aprovado.
                    </p>
                  )}
                </div>
              )}

              {(['homologacao', 'producao'] as AmbienteGateway[]).map((ambiente) => (
                <div key={ambiente} className="space-y-3">
                  <p className="text-sm font-medium text-gray-800">
                    {ambiente === 'homologacao' ? 'Homologação' : 'Produção'}
                    {item.id === 'asaas' ? ' (token manual — fallback)' : ''}
                  </p>
                  <div className="grid gap-3 md:grid-cols-2">
                    {item.campos.map((campo) => (
                      <CampoCredencial
                        key={`${ambiente}-${campo.chave}`}
                        campo={campo}
                        valor={config[ambiente][campo.chave] || ''}
                        onChange={(valor) => atualizarCredencial(item.id, ambiente, campo.chave, valor)}
                      />
                    ))}
                  </div>
                </div>
              ))}

              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-600">Métodos</p>
                <p className="text-sm text-gray-800">{rotuloMetodos(item.metodos)}</p>
                <p className="text-xs font-medium text-gray-600">Endereço do aviso de pagamento</p>
                <p className="text-xs text-gray-500">O teste de conexão usa o ambiente em uso.</p>
                {(['homologacao', 'producao'] as AmbienteGateway[]).map((ambiente) => (
                  <p key={ambiente} className="text-xs text-gray-700 break-all">
                    <span className="font-medium">{ambiente === 'homologacao' ? 'Homologação' : 'Produção'}: </span>
                    {urlWebhook(cnpj, item.id, ambiente)}
                  </p>
                ))}
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => testarConexao(item.id, config.ambienteAtivo)}
                  disabled={testandoId === item.id || salvandoId === item.id}
                  className="btn btn-outline px-4 py-2"
                >
                  {testandoId === item.id ? 'Testando' : 'Testar conexão'}
                </button>
                <button
                  type="button"
                  onClick={() => salvarGateway(item.id)}
                  disabled={salvandoId === item.id || testandoId === item.id}
                  className="btn btn-primary px-4 py-2"
                >
                  {salvandoId === item.id ? 'Salvando' : 'Salvar gateway'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CampoCredencial: React.FC<{
  campo: CampoGateway;
  valor: string;
  onChange: (valor: string) => void;
}> = ({ campo, valor, onChange }) => (
  <label className={`block ${campo.multilinha ? 'md:col-span-2' : ''}`}>
    <span className="text-xs font-medium text-gray-600">{campo.rotulo}</span>
    {campo.multilinha ? (
      <textarea
        className="input mt-1 h-28 py-2 font-mono"
        value={valor}
        onChange={(evento) => onChange(evento.target.value)}
        autoComplete="off"
        spellCheck={false}
      />
    ) : (
      <input
        className="input mt-1"
        type={campo.segredo ? 'password' : 'text'}
        value={valor}
        onChange={(evento) => onChange(evento.target.value)}
        autoComplete="off"
      />
    )}
  </label>
);

export default ConfiguracaoEscola;
